import { Card, Table, TableColumnsType, Spin, Avatar, DatePicker, InputNumber, Button, Space } from "antd";
import React, { useState } from "react";
import { TrophyOutlined, CheckCircleOutlined, CloseCircleOutlined, CarOutlined, DollarOutlined, ReloadOutlined, WalletOutlined } from "@ant-design/icons";
import { IDriverRankingItem } from "models/driverStatistics";
import useDriverStatistics from "hooks/drivers/useDriverStatistics";
import moment from "moment";
import dayjs from "dayjs";

export default function DriverRankings() {
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [limit, setLimit] = useState<number>(10);
  const { statistics, loading, refetch, date: responseDate } = useDriverStatistics(selectedDate, limit);

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedDate(date ? date.format("YYYY-MM-DD") : undefined);
  };

  const handleLimitChange = (value: number | null) => {
    if (value && value > 0 && value <= 50) {
      setLimit(value);
    }
  };

  const createRankingColumns = (): TableColumnsType<IDriverRankingItem> => [
    {
      title: "Rank",
      key: "rank",
      width: 80,
      render: (record: IDriverRankingItem) => {
        const rank = record.rank;
        let color = "";
        if (rank === 1) color = "gold";
        else if (rank === 2) color = "silver";
        else if (rank === 3) color = "#CD7F32"; // bronze
        return (
          <span className="font-bold text-lg" style={{ color }}>
            #{rank}
          </span>
        );
      },
    },
    {
      title: "Driver",
      key: "driver",
      render: (record: IDriverRankingItem) => (
        <div className="flex items-center gap-x-2">
          <Avatar src={record.driver.image} size={40} />
          <div>
            <div className="font-medium">
              {`${record.driver.first_name} ${record.driver.middle_name} ${record.driver.last_name}`}
            </div>
            <div className="text-xs text-gray-500">{record.driver.phone}</div>
            <div className="text-xs text-gray-400">⭐ {record.driver.rate.toFixed(1)}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Metric",
      key: "metric",
      render: (record: IDriverRankingItem) => (
        <span className="font-semibold">{record.metric_label}</span>
      ),
    },
  ];

  if (loading && !statistics) {
    return (
      <div className="w-full flex justify-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-4">Daily Driver Rankings</h1>
        <div className="flex flex-wrap gap-4 items-center mb-4">
          <Space>
            <span className="font-medium">Date:</span>
            <DatePicker
              value={selectedDate ? dayjs(selectedDate) : dayjs()}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              allowClear
              placeholder="Select date (defaults to today)"
            />
          </Space>
          <Space>
            <span className="font-medium">Limit:</span>
            <InputNumber
              min={1}
              max={50}
              value={limit}
              onChange={handleLimitChange}
              style={{ width: 100 }}
            />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={refetch}>
            Refresh
          </Button>
        </div>
        {responseDate && (
          <p className="text-sm text-gray-600">
            Showing rankings for: <span className="font-semibold">{moment(responseDate).format("MMMM DD, YYYY")}</span>
          </p>
        )}
      </div>

      {!statistics && !loading ? (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">No rankings data available</div>
          <div className="text-sm text-gray-400">
            Unable to load driver rankings. Please try refreshing or check the console for error details.
          </div>
        </div>
      ) : null}

      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Most Accepted Requests */}
          <Card
            title={
              <div className="flex items-center gap-x-2">
                <CheckCircleOutlined className="text-green-500 text-lg" />
                <span className="font-semibold">Most Accepted Requests</span>
              </div>
            }
            className="shadow-lg"
            loading={loading}
          >
            <Table
              dataSource={statistics.most_accepted_requests}
              columns={createRankingColumns()}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.driver.id}-accepted`}
            />
          </Card>

          {/* Most Completed Rides */}
          <Card
            title={
              <div className="flex items-center gap-x-2">
                <TrophyOutlined className="text-blue-500 text-lg" />
                <span className="font-semibold">Most Completed Rides</span>
              </div>
            }
            className="shadow-lg"
            loading={loading}
          >
            <Table
              dataSource={statistics.most_completed_rides}
              columns={createRankingColumns()}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.driver.id}-completed`}
            />
          </Card>

          {/* Most Declined Requests */}
          <Card
            title={
              <div className="flex items-center gap-x-2">
                <CloseCircleOutlined className="text-red-500 text-lg" />
                <span className="font-semibold">Most Declined Requests</span>
              </div>
            }
            className="shadow-lg"
            loading={loading}
          >
            <Table
              dataSource={statistics.most_declined_requests}
              columns={createRankingColumns()}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.driver.id}-declined`}
            />
          </Card>

          {/* Insufficient Balance Declines */}
          <Card
            title={
              <div className="flex items-center gap-x-2">
                <WalletOutlined className="text-orange-500 text-lg" />
                <span className="font-semibold">Insufficient Balance Declines</span>
              </div>
            }
            className="shadow-lg"
            loading={loading}
          >
            <Table
              dataSource={statistics.most_insufficient_balance_declines || []}
              columns={createRankingColumns()}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.driver.id}-insufficient-balance`}
            />
          </Card>

          {/* Longest Trip Distance */}
          <Card
            title={
              <div className="flex items-center gap-x-2">
                <CarOutlined className="text-purple-500 text-lg" />
                <span className="font-semibold">Longest Trip Distance</span>
              </div>
            }
            className="shadow-lg"
            loading={loading}
          >
            <Table
              dataSource={statistics.longest_trip_distance}
              columns={createRankingColumns()}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.driver.id}-distance`}
            />
          </Card>

          {/* Highest Earnings */}
          <Card
            title={
              <div className="flex items-center gap-x-2">
                <DollarOutlined className="text-green-600 text-lg" />
                <span className="font-semibold">Highest Earnings</span>
              </div>
            }
            className="shadow-lg"
            loading={loading}
          >
            <Table
              dataSource={statistics.highest_earnings}
              columns={createRankingColumns()}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.driver.id}-earnings`}
            />
          </Card>
        </div>
      )}
    </div>
  );
}

